import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import type { ReactNode } from "react";
import { useState } from "react";
import { timbroamAudioEngine } from "./audio/AudioEngine";
import { TopBar } from "./components/TopBar";
import { getInstrumentById } from "./data/instruments";
import { LocaleProvider } from "./i18n/LocaleContext";
import { LocalizedSeo } from "./i18n/LocalizedSeo";
import { getLocaleFromPath, isLocaleCode } from "./i18n/locales";
import { useTimbroamState } from "./lib/appState";
import { AboutPage } from "./pages/AboutPage";
import { AudioLabPage } from "./pages/AudioLabPage";
import { InstrumentsPage } from "./pages/InstrumentsPage";
import { PlayPage } from "./pages/PlayPage";
import { QAPage } from "./pages/QAPage";

function LocaleRoute({ children }: { children: ReactNode }) {
  const { locale } = useParams();

  if (!isLocaleCode(locale)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export default function App() {
  const { state, dispatch } = useTimbroamState();
  const location = useLocation();
  const locale = getLocaleFromPath(location.pathname);
  const [audioFailed, setAudioFailed] = useState(false);
  const selectedInstrument = getInstrumentById(state.selectedInstrumentId);

  const handleEnableAudio = async () => {
    try {
      const ready = await timbroamAudioEngine.ensureReady();
      setAudioFailed(!ready);
      dispatch({ type: "setAudioReady", audioReady: ready });
    } catch {
      setAudioFailed(true);
      dispatch({ type: "setAudioReady", audioReady: false });
    }
  };
  const audioState = audioFailed ? "failed" : state.audioReady ? "ready" : "locked";
  const pathWithoutLocale = location.pathname.replace(/^\/[a-z]{2}(?:\/|$)/, "/");
  const isPlayPage = pathWithoutLocale === "/" || pathWithoutLocale === "";

  return (
    <LocaleProvider locale={locale}>
      <LocalizedSeo />
      <div className="app-shell">
        <TopBar
          selectedInstrument={selectedInstrument}
          audioState={audioState}
          onEnableAudio={() => void handleEnableAudio()}
          showInstrumentBar={isPlayPage}
        />
        <main className="app-main">
          <Routes>
            <Route path="/" element={<PlayPage />} />
            <Route path="/instruments" element={<InstrumentsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/qa" element={<QAPage />} />
            <Route path="/audio-lab" element={<AudioLabPage />} />
            <Route path="/:locale" element={<LocaleRoute><PlayPage /></LocaleRoute>} />
            <Route path="/:locale/instruments" element={<LocaleRoute><InstrumentsPage /></LocaleRoute>} />
            <Route path="/:locale/about" element={<LocaleRoute><AboutPage /></LocaleRoute>} />
            <Route path="/:locale/qa" element={<LocaleRoute><QAPage /></LocaleRoute>} />
            <Route path="/:locale/audio-lab" element={<LocaleRoute><AudioLabPage /></LocaleRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </LocaleProvider>
  );
}
